/**
 * jobRegistry — single source of truth for how each background-job TYPE is
 * presented (label, icon, the toast we show on submit) and what its completion
 * DOES (the CTA derived from the terminal job's result envelope).
 *
 * Leaf module on purpose: it imports nothing from contexts/hooks so it can be
 * pulled in by the banner, the provider, and the modals without risking an
 * indirect circular import (memory: context-file import cycles → blank screen).
 *
 * A new long-running process becomes fire-and-forget by adding (a) a backend
 * job handler and (b) one entry here.
 */

// ── Deep-link href builders ──────────────────────────────────────────────
// SPA-internal routes start with "/" (the ToastProvider/banner navigate to
// them via react-router). External signed URLs (http…) are opened/downloaded.

export function docHref(caseId, documentId) {
  if (!caseId || !documentId) return null;
  // DocumentDeepLink (app-wide) opens the viewer from a `?doc=<id>` query.
  return `/case/${caseId}/inventory?doc=${documentId}`;
}

export function checklistHref(caseId) {
  if (!caseId) return null;
  // Importing makes the new checklist active; the checklist tab shows it.
  return `/case/${caseId}/checklist`;
}

/**
 * JOB_REGISTRY — keyed by job_type.
 *   label         human label for the banner row + toasts
 *   icon          emoji shown in the toast
 *   submitMessage the "we'll let you know" toast shown on submit
 *   completion(result, { caseId }) → {
 *       message, cta?, href?, secondary?: { cta, href }, autoDownload?
 *   }
 *
 * Job types WITHOUT a `completion` (index_documents, complete_extraction, …)
 * keep the banner's existing aggregate rendering and never fire a toast.
 */
export const JOB_REGISTRY = {
  excerpt_media: {
    label: 'Cutting excerpt',
    icon: '✂️',
    submitMessage: 'Cutting the excerpt… we’ll let you know when it’s ready. It is transcribed in full on its own.',
    completion: (r, { caseId } = {}) => {
      const base = { message: r?.message || 'Excerpt ready.' };
      if (!r?.output_document_id) return base;
      return {
        ...base,
        cta: 'View excerpt',
        href: docHref(caseId, r.output_document_id),
        secondary: r.download_url ? { cta: 'Download', href: r.download_url } : null,
      };
    },
  },
  redact_media: {
    label: 'Rendering redacted copy',
    icon: '🎬',
    submitMessage: 'Rendering redacted copy… we’ll let you know when it’s ready.',
    completion: (r, { caseId } = {}) => {
      const base = { message: r?.message || 'Redacted copy ready.' };
      if (!r?.output_document_id) return base; // "no redactions to render" path
      return {
        ...base,
        cta: 'View redacted copy',
        href: docHref(caseId, r.output_document_id),
        secondary: r.download_url ? { cta: 'Download', href: r.download_url } : null,
      };
    },
  },
  export_documents: {
    label: 'Exporting documents',
    icon: '📦',
    submitMessage: 'Preparing your download… it will be under Downloads when it’s ready.',
    completion: (r) => ({
      message: r?.message || 'Export ready.',
      cta: r?.download_url ? 'Download' : null,
      href: r?.download_url || null,
      // Preserve the legacy auto-download UX (Export users expect the file to
      // land on completion) even when the originating modal is long gone.
      autoDownload: Boolean(r?.download_url),
      // The notice stays until it is dismissed (it used to leave after 12 s),
      // and the export stays under Downloads whether or not anyone saw this.
      sticky: true,
      secondary: { cta: 'Open Downloads', event: 'dc:open-downloads' },
    }),
    // while it runs: "Preparing 212 of 421" from the job's progress
    progress: (p) => (p?.total ? { done: p.done || 0, total: p.total, verb: 'Preparing' } : null),
  },
  chronology_arc_dates: {
    label: 'Arc date pass (chronology)',
    icon: '✨',
    submitMessage: 'Arc is reading sources to suggest dates… we’ll let you know.',
    completion: (r, { caseId } = {}) => ({
      message: r?.status === 'cancelled'
        ? 'Arc date pass cancelled.'
        : `Arc suggested dates for ${r?.suggested ?? 0} of ${r?.scanned ?? 0} entr${(r?.scanned ?? 0) === 1 ? 'y' : 'ies'}${r?.skipped ? ` (${r.skipped} skipped)` : ''}.`,
      cta: 'Review in Chronology',
      href: caseId ? `/case/${caseId}/chronology` : null,
    }),
  },
  chronology_nest_pass: {
    label: 'Organizing into episodes',
    icon: '✨',
    submitMessage: 'Arc is organizing the chronology into episodes… we’ll let you know.',
    completion: (r, { caseId } = {}) => ({
      message: r?.status === 'cancelled'
        ? 'Organize pass cancelled.'
        : `Organized ${r?.nested ?? 0} entr${(r?.nested ?? 0) === 1 ? 'y' : 'ies'} under ${r?.episodes ?? 0} episode${(r?.episodes ?? 0) === 1 ? '' : 's'}${r?.skipped_undated ? ` (${r.skipped_undated} undated skipped)` : ''}. Un-nest anything filed wrong.`,
      cta: 'Review in Chronology',
      href: caseId ? `/case/${caseId}/chronology` : null,
    }),
  },
  import_checklist: {
    label: 'Importing checklist',
    icon: '✅',
    submitMessage: 'Importing checklist… we’ll let you know when it’s ready.',
    completion: (r, { caseId } = {}) => {
      const n = r?.items_created;
      return {
        message: r?.message
          || `Checklist imported${n ? ` — ${n} item${n === 1 ? '' : 's'}` : ''}.`,
        cta: 'View checklist',
        href: checklistHref(caseId),
      };
    },
  },
};

export function jobLabel(type) {
  return JOB_REGISTRY[type]?.label || type?.replace(/_/g, ' ') || 'Background job';
}

export function jobIcon(type) {
  return JOB_REGISTRY[type]?.icon || null;
}

export function jobSubmitMessage(type) {
  return (
    JOB_REGISTRY[type]?.submitMessage
    || `${jobLabel(type)}… we’ll let you know when it’s ready.`
  );
}

/** Compute the completion CTA for a terminal job's result envelope. */
export function jobCompletion(type, result, ctx) {
  const reg = JOB_REGISTRY[type];
  if (!reg?.completion) return null;
  try {
    return reg.completion(result || {}, ctx || {});
  } catch {
    return null;
  }
}

/** True for job types whose completion we surface as a toast + banner CTA. */
export function hasCompletion(type) {
  return Boolean(JOB_REGISTRY[type]?.completion);
}
